// Verwaltung: Schalter „Mieterportal“ bzw. „Eigentümerportal“ mit Erklärung (Liste Mieter/Eigentümer).
// Ändern nur Admins (die Datenbank prüft das zusätzlich); alle anderen sehen den Zustand.
import { useEffect, useState } from 'react';
import { CaretDown, CaretUp, Globe } from '@phosphor-icons/react';
import { IosSchalter } from '../ui/IosSchalter';
import { supabase } from '../../lib/supabase/client';
import { PORTAL_ERKLAERUNG, ladePortalSchalter, speicherePortalSchalter, type PortalArt } from '../../lib/portal/schalter';

export default function PortalSchalterKarte({ art, onMeldung }: { art: PortalArt; onMeldung?: (text: string, fehler?: boolean) => void }) {
  const [an, setAn] = useState<boolean | null>(null);
  const [testkonten, setTestkonten] = useState(0);
  const [eingerichtet, setEingerichtet] = useState(true);
  const [admin, setAdmin] = useState(false);
  const [offen, setOffen] = useState(false);
  const [speichert, setSpeichert] = useState(false);
  const e = PORTAL_ERKLAERUNG[art];

  useEffect(() => {
    let aktiv = true;
    void Promise.all([ladePortalSchalter(), supabase.rpc('is_admin')]).then(([s, a]) => {
      if (!aktiv) return;
      setEingerichtet(!!s);
      setAn(s ? s[art] : false);
      setTestkonten(s?.testkonten ?? 0);
      setAdmin(a.data === true);
    });
    return () => { aktiv = false; };
  }, [art]);

  const umschalten = async (neu: boolean) => {
    setSpeichert(true);
    const fehler = await speicherePortalSchalter(art, neu);
    setSpeichert(false);
    if (fehler) { onMeldung?.(`${e.titel} konnte nicht umgeschaltet werden: ${fehler}`, true); return; }
    setAn(neu);
    onMeldung?.(`${e.titel} ist jetzt ${neu ? 'eingeschaltet' : 'ausgeschaltet'}.`);
  };

  let zustand = an ? 'Eingeschaltet' : e.aus;
  if (!an && testkonten > 0) zustand = `${e.aus} Einzeln freigeschaltet (Testphase): ${testkonten === 1 ? '1 Person' : `${testkonten} Personen`} – Schalter im Bereich „Portal“ der Person.`;
  if (!eingerichtet) zustand = 'Noch nicht eingerichtet – wird mit der Datenbank-Freigabe angelegt.';
  let tip = an ? `${e.titel} ausschalten` : `${e.titel} einschalten`;
  if (!eingerichtet) tip = 'Noch nicht eingerichtet';
  else if (!admin) tip = 'Nur Admins können das Portal ein- oder ausschalten';

  return (
    <section className="immo-glas flex flex-col" style={{ borderRadius: 22, padding: '12px 14px', gap: 8 }} aria-label={e.titel}>
      <div className="flex items-center" style={{ gap: 12 }}>
        <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 34, height: 34, borderRadius: 10, background: 'var(--acc-soft)', color: 'var(--acc)' }}>
          <Globe size={18} weight="fill" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col" style={{ gap: 1 }}>
          <span style={{ fontSize: 14, fontWeight: 800 }}>{e.titel}</span>
          <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--ink3)' }}>{zustand}</span>
        </span>
        <button type="button" onClick={() => setOffen((o) => !o)} aria-expanded={offen} data-tip="Was das Portal zeigt und was nicht"
          className="inline-flex shrink-0 items-center" style={{ gap: 4, border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: 'var(--ink2)', padding: '6px 8px', borderRadius: 10 }}>
          Erklärung {offen ? <CaretUp size={12} weight="bold" /> : <CaretDown size={12} weight="bold" />}
        </button>
        <IosSchalter an={!!an} onChange={(neu) => void umschalten(neu)} label={e.titel} gesperrt={!eingerichtet || !admin || speichert || an === null} tip={tip} />
      </div>
      {offen && (
        <div className="flex flex-col" style={{ gap: 6, fontSize: 13, lineHeight: 1.5, color: 'var(--ink2)', paddingLeft: 46 }}>
          <span><b style={{ color: 'var(--ink)' }}>Eingeschaltet: </b>{e.was}</span>
          <span><b style={{ color: 'var(--ink)' }}>Nicht: </b>{e.nicht}</span>
          <span><b style={{ color: 'var(--ink)' }}>Ausgeschaltet: </b>{e.aus}</span>
        </div>
      )}
    </section>
  );
}
